import { Bilingual, BeatConfig } from './protocols';

export interface GuidedPhase {
  title: Bilingual;
  minutes: number;
  instruction: Bilingual;
  cue: Bilingual;
  breath: boolean;
}

export interface GuidedSession {
  id: string;
  name: Bilingual;
  subtitle: Bilingual;
  durationLabel: Bilingual;
  phases: GuidedPhase[];
  sound: BeatConfig;
  ambientOnly: boolean;
  minimalSound: boolean;
  closingNote: Bilingual;
}

export const GUIDED_SESSIONS: Record<string, GuidedSession> = {
  heartBrain: {
    id: 'heartBrain',
    name: { no: 'Hjerte-hjerne-synkronisering', en: 'Heart-Brain Synchronisation' },
    subtitle: {
      no: 'Koherenspust med hjerte-fokus og følt takknemlighet, til mottak.',
      en: 'Coherence breath with heart focus and felt gratitude, into reception.',
    },
    durationLabel: { no: '15 minutter', en: '15 minutes' },
    phases: [
      {
        title: { no: 'Fysisk anker', en: 'Physical Anchor' },
        minutes: 2,
        breath: true,
        instruction: {
          no: 'Legg den dominante hånden på hjertet. Lukk øynene. Kjenn vekten og varmen av hånden mot brystet. Begynn koherenspusten (5 inn gjennom nesen, 5 ut gjennom nesen). Den fysiske kontakten er ikke symbolsk — den aktiverer berøringssansen og bekrefter oppmerksomheten, noe som fremskynder koherens. Behold hånden her gjennom hele økten.',
          en: 'Place your dominant hand on your heart. Close your eyes. Feel the weight and warmth of your hand on your chest. Begin the coherence breath (5 in through the nose, 5 out through the nose). The physical contact is not symbolic — it activates touch receptors and confirms your focus, which speeds coherence. Keep your hand here for the whole session.',
        },
        cue: { no: 'Hånd på hjertet. Pust 5 inn, 5 ut.', en: 'Hand on heart. Breathe 5 in, 5 out.' },
      },
      {
        title: { no: 'Emosjonell aktivering', en: 'Emotional Activation' },
        minutes: 3,
        breath: true,
        instruction: {
          no: 'Mens du holder pusten jevn, fremkall en ekte følelse av takknemlighet, omsorg eller verdsettelse — ikke en tanke om noe du verdsetter, men en følt fornemmelse i brystet. Hvis ingen følelse er tilgjengelig, bruk minnet om et øyeblikk av ekte omsorg; minnet aktiverer den samme hjertekretsen. Ikke spill en følelse som ikke er der.',
          en: 'While keeping the breath steady, generate a genuine feeling of gratitude, care, or appreciation — not a thought about something you appreciate, but a felt sensation in the chest. If no feeling is available, use the memory of a moment of genuine care; the memory activates the same cardiac circuitry. Do not perform an emotion that isn’t there.',
        },
        cue: { no: 'Kjenn ekte takknemlighet i brystet.', en: 'Feel genuine gratitude in the chest.' },
      },
      {
        title: { no: 'Feltutvidelse', en: 'Field Expansion' },
        minutes: 5,
        breath: true,
        instruction: {
          no: 'La følelsen av verdsettelse utvide seg naturlig utover fra hjertet — først fyll brystet helt, så hele kroppen, så utover kroppens grense til rommet rundt. Du fabrikkerer ikke dette; du retter oppmerksomheten mot et felt som allerede finnes. Behold pusten. Utvidelsen skal føles uanstrengt; hvis den krever anstrengelse, vend tilbake til brystet og la feltet etablere seg på nytt derfra.',
          en: 'Let the feeling of appreciation expand naturally outward from the heart — first filling the chest, then the whole body, then beyond the body’s edge into the space around you. You are not fabricating this; you are directing attention to a field that already exists. Keep the breath. The expansion should feel effortless; if it takes effort, return to the chest and let the field re-establish from there.',
        },
        cue: { no: 'La følelsen utvide seg utover hjertet.', en: 'Let the feeling expand beyond the heart.' },
      },
      {
        title: { no: 'Mottak', en: 'Reception' },
        minutes: 5,
        breath: false,
        instruction: {
          no: 'Nå: slutt å skape, begynn å motta. Slipp den villede følelsen. La pusten finne sin egen rytme uten styring. Bare vær til stede i det utvidede feltet uten å styre noe. Dette er mottaksfasen — der Tilstanden oftest først berøres. Den vanligste feilen er trangen til å «sjekke» om den har kommet; det oppløser betingelsene. Motta uten å måle.',
          en: 'Now: stop generating and start receiving. Release the intentional feeling. Let the breath find its own rhythm without guidance. Simply be present in the expanded field without directing anything. This is the reception phase — where The State is most often first touched. The most common error is the urge to “check” whether it has arrived; that dissolves the conditions. Receive without measuring.',
        },
        cue: { no: 'Slutt å skape. Motta uten å måle.', en: 'Stop generating. Receive without measuring.' },
      },
    ],
    sound: { carrierHz: 528, beatHz: 8 },
    ambientOnly: false,
    minimalSound: false,
    closingNote: {
      no: 'De tre første fasene skaper betingelsene; mottak er der Tilstanden ankommer.',
      en: 'The first three phases create the conditions; reception is where The State arrives.',
    },
  },

  fieldExpansion: {
    id: 'fieldExpansion',
    name: { no: 'Gradvis feltutvidelse', en: 'Graduated Field Expansion' },
    subtitle: {
      no: 'Utvid bevisstheten trinnvis fra kroppen til det åpne, grenseløse feltet.',
      en: 'Expand awareness step by step from the body to the open, boundless field.',
    },
    durationLabel: { no: '15 minutter', en: '15 minutes' },
    phases: [
      {
        title: { no: 'Kroppsgrense', en: 'Body Boundary' },
        minutes: 3,
        breath: false,
        instruction: {
          no: 'Sitt stille med lukkede øyne. Rett oppmerksomheten mot hele hudens overflate samtidig — ikke én bestemt fornemmelse, men hele grensen på én gang. Dette er samtidig, utvidet oppmerksomhet i stedet for den sekvensielle, lineære oppmerksomheten sinnet vanligvis bruker. Det kan føles uvant først. Å holde hele kroppsgrensen samtidig krever en videre, mindre fokusert kvalitet — mer som et felt enn en stråle.',
          en: 'Sit still with eyes closed. Direct attention to the entire surface of your skin at once — not any single sensation, but the whole boundary simultaneously. This is expanded, simultaneous awareness rather than the sequential attention the mind usually uses. It may feel unusual at first. Holding the whole body boundary at once needs a wider, less focused quality — more like a field than a beam.',
        },
        cue: { no: 'Kjenn hele hudens overflate på én gang.', en: 'Feel the whole skin surface at once.' },
      },
      {
        title: { no: 'Nært felt', en: 'Immediate Field' },
        minutes: 3,
        breath: false,
        instruction: {
          no: 'Utvid oppmerksomheten til å omfatte rommet like rundt kroppen — luften mot huden, temperaturen, lydene like ved. Du flytter ikke oppmerksomheten sekvensielt utover; du utvider beholderen av oppmerksomhet til å inkludere mer samtidig. Behold kroppsgrensen som en del av det du er bevisst på, nå utvidet til å inkludere det nære rommet rundt.',
          en: 'Expand awareness to include the space right around your body — the air against your skin, the temperature, the sounds nearby. You are not moving attention outward step by step; you are widening the container of awareness to include more at once. Keep the body boundary as part of what you’re aware of, now expanded to include the immediate surround.',
        },
        cue: { no: 'Utvid til rommet like rundt kroppen.', en: 'Expand to the space just around you.' },
      },
      {
        title: { no: 'Romfelt', en: 'Room Field' },
        minutes: 4,
        breath: false,
        instruction: {
          no: 'Utvid oppmerksomheten til å omfatte hele rommet som en enhet — dets dimensjoner, lydene fra alle retninger samtidig, følelsen av rommet som ett felt heller enn en samling objekter. Behold kroppen som ett element i dette større feltet — ikke ekskludert, ikke sentrum, men inkludert som én node. Forvent mild motstand her. Ikke tving deg forbi den; vær til stede med ubehaget av en kjent grense som utfordres.',
          en: 'Expand awareness to include the whole room as a single whole — its dimensions, the sounds arriving from all directions at once, the sense of the space as one field rather than a set of separate objects. Keep the body as one element within this larger field — not excluded, not the center, but included as one node. Expect mild resistance here. Don’t force past it; stay present with the discomfort of a familiar boundary being questioned.',
        },
        cue: { no: 'La bevisstheten fylle hele rommet.', en: 'Let awareness fill the whole room.' },
      },
      {
        title: { no: 'Åpent felt', en: 'Open Field' },
        minutes: 5,
        breath: false,
        instruction: {
          no: 'Slipp den villede utvidelsen helt. Ikke prøv å strekke bevisstheten lenger, og ikke prøv å holde noen bestemt grense. Bare vær til stede uten å sette en grense rundt hva «du» inkluderer — uten å trekke linjen ved huden. Sinnet vil forsøke å gjenopprette den vanlige grensen. Når det skjer, legg merke til det uten å følge det, og vend mildt tilbake til åpen, grenseløs oppmerksomhet. Denne tilbakevendingen er praksisen. Tilstanden ankommer mest pålitelig i dette åpne, ustyrte feltet.',
          en: 'Release the guided expansion entirely. Don’t try to extend awareness further, and don’t try to hold any particular boundary. Simply be present without placing a limit around what “you” includes — without drawing the line at the skin. The mind will try to reassert the ordinary boundary. When it does, notice it without following, then gently return to open, boundary-undetermined awareness. This return is the practice. The State arrives most reliably in this open, undirected field.',
        },
        cue: { no: 'Ingen grense. Vend tilbake til åpen bevissthet.', en: 'No boundary. Return to open awareness.' },
      },
    ],
    sound: { carrierHz: 432, beatHz: 8 },
    ambientOnly: true,
    minimalSound: false,
    closingNote: {
      no: 'Hver tilbakevending bygger en ny bane: bevisstheten oppdager at selvgrensen er en vane, ikke et faktum.',
      en: 'Each return builds a new pathway: awareness discovering the self-boundary is a habit, not a fact.',
    },
  },

  stillness: {
    id: 'stillness',
    name: { no: 'Stillhet', en: 'Stillness' },
    subtitle: {
      no: 'Ikke-innblanding: sitt, gjør ingenting, vær til stede med det som er.',
      en: 'Non-interference: sit, do nothing, be present with what is.',
    },
    durationLabel: { no: '22 minutter', en: '22 minutes' },
    phases: [
      {
        title: { no: 'Stillhet', en: 'Stillness' },
        minutes: 20,
        breath: false,
        instruction: {
          no: 'Sitt komfortabelt. Lukk øynene. Hendene i fanget. Og gjør ingenting. Ikke styr pusten. Ikke rett oppmerksomheten mot eller bort fra noe. Ikke sjekk om Tilstanden har kommet. Bare vær til stede med det som er — inkludert kjedsomhet, uro og trangen til å gjøre noe mer produktivt. Når tanker oppstår — og de vil — verken følg dem eller kjemp mot dem. Legg bare merke til at de oppstår, uten å engasjere innholdet. Hvis Tilstanden kommer, viser den seg som et stille skift — en litt dypere tilstedeværelse, varme i brystet, en grense som blir mindre bestemt. Ikke grip etter den. Å gripe avslutter den; å motta lar den utdype seg.',
          en: 'Sit comfortably. Close your eyes. Hands in your lap. And do nothing. Don’t guide the breath. Don’t direct attention toward or away from anything. Don’t check whether The State has arrived. Simply be present with whatever is here — including boredom, restlessness, and the urge to do something more productive. When thoughts arise — and they will — neither follow them nor fight them. Just notice them arising, without engaging their content. If The State comes, it shows up as a quiet shift — a slightly deeper presence, warmth in the chest, a boundary becoming less definite. Don’t grasp at it. Grasping ends it; receiving lets it deepen.',
        },
        cue: { no: 'Gjør ingenting. Vær til stede med det som er.', en: 'Do nothing. Be present with what is.' },
      },
      {
        title: { no: 'Overgang ut', en: 'Transition out' },
        minutes: 2,
        breath: false,
        instruction: {
          no: 'Når de 20 minuttene er over, ikke åpne øynene og gjenoppta aktivitet med en gang. Sitt i to minutter til før du beveger deg. Åpne øynene sakte. Ta med kvaliteten av ikke-innblanding inn i de første bevegelsene. Beveg deg uten hast. Det er her Tilstanden begynner å sive inn i dagliglivet i stedet for å være begrenset til øvingsrommet.',
          en: 'When the 20 minutes are over, don’t open your eyes and resume activity at once. Sit for two more minutes before moving. Open your eyes slowly. Carry the quality of non-interference into your first movements. Move without urgency. This is where The State begins to seep into daily life rather than staying confined to the practice room.',
        },
        cue: { no: 'Overgang. Beveg deg sakte, uten hast.', en: 'Transition. Move slowly, without urgency.' },
      },
    ],
    sound: { carrierHz: 432, beatHz: 8 },
    ambientOnly: true,
    minimalSound: true,
    closingNote: {
      no: 'Overgangen lærer nervesystemet at Tilstanden ikke er forbeholdt formell sitting.',
      en: 'The transition teaches the nervous system that The State is not exclusive to formal sitting.',
    },
  },
};

export function getSession(id: string): GuidedSession | undefined {
  return GUIDED_SESSIONS[id];
}
